/* eslint-disable max-lines */
import {
	activeBoxKey,
	frequencyKey,
	CART_ID,
	SUBSCRIPTION_DISCOUNT_CODE_KEY,
	CHECKOUT_DOMAIN,
	littledataPixelIds,
	freeGiftRemovedKey,
	SHOPIFY_DOMAIN,
	SHOPIFY_API_VERSION,
	SHOPIFY_STOREFRONT_TOKEN,
} from "utils/constants";
import { findClosestBox, getExistingCartId, getOrCreateCartId, mapCart } from "utils/cart";
import { trackBeginCheckout } from "services/analytics";
import checkoutQueryString from "helpers/checkout-query-string";
import littledata from "@littledata/headless-shopify-sdk";
import * as CookieConsent from "vanilla-cookieconsent";
import extractIdFromGid from "helpers/extract-gid";
import { selectBox, selectMinBox, setDeliveryFrequency, resetRemoveCount } from "slices/cart";
import { setOneTimeSession } from "slices/global";
import { createAsyncThunk } from "@reduxjs/toolkit";
import isBundleWithSubProducts, { getBundleSubproductLines } from "utils/bundle";
import { isVariant1165V1, isVariant1165V2 } from "utils/abtest";
import { getLanguageCartAttributes } from "utils/language";
import {
	clearOneTimeSession,
	getCheckoutFlowAttributes,
	getOneTimeFlowAttributes,
	hasDedicatedOneTimeFlowItems,
} from "utils/one-time-session";

const buildCheckoutUrl = ({ checkoutUrl, hostnameOverride, language, redirectedDiscount, queryString }) => {
	const discountParam = redirectedDiscount ? `&discount=${encodeURIComponent(redirectedDiscount)}` : "";
	const url = checkoutUrl + "&locale=" + language + discountParam + queryString;
	const parsed = (() => { try { return new URL(url); } catch (_) { return false; } })();
	return parsed ? url.replace(parsed.hostname, hostnameOverride) : url;
};

const getBundleVariantGidFromBundleItem = (bundleItem) => {
	const firstSub = bundleItem?.subproducts?.[0];
	return firstSub?.attributes?.find?.((a) => a.key === "_bid")?.value || null;
};

const resetOneTimeSessionIfCartEmpty = (cart, dispatch) => {
	if (!cart || Object.keys(cart.items || {}).length) return;
	clearOneTimeSession(); dispatch(setOneTimeSession(false));
};

const fetchSellingPlanAllocationsByVariantId = async (variantIds = []) => {
	if (!variantIds.length) return {};

	try {
		const res = await fetch(`https://${SHOPIFY_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
			},
			body: JSON.stringify({
				query: `
					query getVariantSellingPlans($ids: [ID!]!) {
						nodes(ids: $ids) {
							... on ProductVariant {
								id
								sellingPlanAllocations(first: 20) {
									nodes {
										sellingPlan { id name }
									}
								}
							}
						}
					}
				`,
				variables: { ids: variantIds },
			}),
		});

		const json = await res.json();
		const nodes = json?.data?.nodes || [];
		const map = {};

		for (const node of nodes) {
			if (!node?.id) continue;
			map[node.id] = node?.sellingPlanAllocations?.nodes || [];
		}

		return map;
	} catch (e) {
		console.error("[beginCheckout] Failed to fetch selling plan allocations", e);
		return {};
	}
};

export const checkForCartBonus = () => async (dispatch, getState) => {
	const { cartBonus, isOneTimeSession, isOneTimeCollection } = getState().global;
	const { totalProgress, items } = getState().cart;
	if (!cartBonus) return;
	const shouldSuppressBonus = isOneTimeSession || isOneTimeCollection;

	const freeGiftRemoved = sessionStorage.getItem(freeGiftRemovedKey);
	if (freeGiftRemoved === cartBonus.product.variant.id) return;
	const { id, stock } = cartBonus.product.variant;
	const cartItem = items[id], meetsThreshold = totalProgress >= cartBonus.weightThreshold;

	if (cartItem) {
		if (!meetsThreshold || shouldSuppressBonus) await dispatch(removeCartItem({ id: cartItem.tempId, source: "bonus" }));
		// Ensure the free gift is only added once
		if (cartItem.quantity > 1) {
			await dispatch(updateCartItem({ id: cartItem.tempId, quantity: 1, source: "bonus" }));
		}
	} else if (meetsThreshold && !shouldSuppressBonus) {
		await dispatch(addCartItem({ variant: { id, stock }, source: "bonus" }));
	}
};

export const fetchCart = createAsyncThunk("cart/fetchCart", async (discountCode, thunkAPI) => {
	const { getState, dispatch } = thunkAPI;
	const { shopifyClient } = getState().global;

	const cartId = getExistingCartId();
	let cart;

	if (cartId) cart = await shopifyClient.cart.fetch(cartId);

	if (!cart?.id) {
		localStorage.removeItem(CART_ID);
		localStorage.removeItem("littledataIDs");

		const input = {}; if (discountCode) input.discountCodes = [discountCode];
		cart = await shopifyClient.cart.create(input);
		localStorage.setItem(CART_ID, cart.id);
		if (discountCode) localStorage.setItem(SUBSCRIPTION_DISCOUNT_CODE_KEY, discountCode);
	} else if (discountCode) {
		const previousCode = localStorage.getItem(SUBSCRIPTION_DISCOUNT_CODE_KEY);
		const cartCodes = (cart.discountCodes || []).map(d => d.code);
		const hasCurrentCode = cartCodes.includes(discountCode);
		const hasStalePreviousCode = previousCode && previousCode !== discountCode && cartCodes.includes(previousCode);

		if (!hasCurrentCode || hasStalePreviousCode) {
			const updatedCodes = [
				discountCode,
				...cartCodes.filter(c => c !== discountCode && c !== previousCode),
			];
			cart = await shopifyClient.cart.updateDiscountCodes(cartId, updatedCodes);
		}

		localStorage.setItem(SUBSCRIPTION_DISCOUNT_CODE_KEY, discountCode);
	}

	const updatedCart = await mapCart(cart);

	console.log({ updatedCart });

	dispatch(addLittledataAttributes(updatedCart));

	return updatedCart;
});

export const addLittledataAttributes = (cart) => async (_, getState) => {
	const { acceptedCategories } = CookieConsent.getUserPreferences();
	if (!acceptedCategories.includes("analytics_storage"))
		return;

	const { shopifyClient } = getState().global;
	const { id: cartId, attributes = [] } = cart || getState().cart || {};

	if (!cartId || attributes.find(attr => attr.key === "_fbp-clientID")) {
		return;
	}

	try {
		const littledataNoteAttributes = await littledata.fetchClientIds(littledataPixelIds) || [];
		const pureCartId = extractIdFromGid(cartId).split("?")[0];
		await Promise.all([
			littledata.sendCartToLittledata(pureCartId),
			shopifyClient.cart.updateAttributes(cartId, [...littledataNoteAttributes, { key: "cartID", value: pureCartId }])
		]);

	} catch (error) {
		console.error("An error occurred while adding cart attributes:", error);
		return [];
	}
};

export const beginCheckout = createAsyncThunk(
	"cart/beginCheckout",
	async (
		{ language, isOneTimePurchase, autoDiscountCode, subscriptionDiscount, customSellingPlanId },
		{ getState }
	) => {
		const state = getState();
		const cart = state.cart;
		const { shopifyClient } = state.global;
		const { isAuthenticated, email, defaultAddress } = state.account.profile;

		const queryString = isAuthenticated ? `&${checkoutQueryString(email, defaultAddress)}` : "";
		const items = Object.values(cart.items);

		const sellingPlanId = customSellingPlanId || cart.delivery?.value;
		const subscriptionSeedCode = autoDiscountCode || subscriptionDiscount, nonSubscriptionDiscountCodes = (cart.discountCodes || [])
			.filter((code) => code && code !== subscriptionSeedCode);
		const redirectedDiscount = isOneTimePurchase
			? nonSubscriptionDiscountCodes[0] || null
			: nonSubscriptionDiscountCodes[0] || subscriptionDiscount || null;

		// create a dedicated "checkout cart" that uses bundle SKUs (UI cart stays de-bundled/grouped)
		// IMPORTANT:
		// we pull the bundle variant GID from the `_bid` attribute stored on the subproduct lines
		const checkoutLinesDraft = items
			.map((item) => {
				const isBundle = isBundleWithSubProducts(item);
				const merchandiseId = isBundle ? getBundleVariantGidFromBundleItem(item) : item?.variant?.id;
				if (!merchandiseId) return null;

				const isFreeGift = item?.isFreeGift || item?.attributes?.some(attr => attr.key === "_freeGift"),
					isOneTimeLine = item?.oneTime || item?.attributes?.some(attr => attr.key === "_oneTime");

				return {
					merchandiseId,
					quantity: item.quantity,
					attributes: item.attributes || [],
					__isFreeGift: isFreeGift,
					__isOneTime: isOneTimeLine,
					__sellingPlanAllocations: item?.variant?.sellingPlanAllocations,
					__subproducts: isBundle ? item?.subproducts : undefined,
				};
			})
			.filter(Boolean);

		const getSubproductUnitPrice = (sub) => {
			const raw = sub?.variant?.price;
			if (raw === null || raw === undefined) return NaN;
			if (typeof raw === "object" && raw.amount !== undefined) return parseFloat(String(raw.amount)) || 0;
			return parseFloat(String(raw)) || 0;
		};

		const isFreeGiftSubproduct = (sub) => {
			const rawWeight = sub?.variant?.weight;
			const weightInGrams = Number.isFinite(rawWeight)
				? (rawWeight < 10 ? rawWeight * 1000 : rawWeight)
				: null;
			if (weightInGrams === null || weightInGrams >= 1000) return false;
			const price = getSubproductUnitPrice(sub);
			if (!Number.isFinite(price)) return false;
			return price === 0;
		};

		for (const line of [...checkoutLinesDraft]) {
			if (!Array.isArray(line.__subproducts) || !line.__subproducts.length) continue;

			for (const subproduct of line.__subproducts) {
				if (!isFreeGiftSubproduct(subproduct)) continue;
				const variantId = subproduct?.variant?.id;
				if (!variantId) continue;

				const existing = checkoutLinesDraft.find((l) => l.merchandiseId === variantId);
				if (existing) {
					existing.quantity += subproduct.quantity;
				} else {
					checkoutLinesDraft.push({
						merchandiseId: variantId,
						quantity: subproduct.quantity,
						attributes: [{ key: "_freeGift", value: "true" }],
						__isFreeGift: true,
						__sellingPlanAllocations: [],
					});
				}
			}
		}

		const variantIdsNeedingPlans = sellingPlanId
			? checkoutLinesDraft
				.filter((l) => !l.__isFreeGift && !l.__isOneTime &&
					(!Array.isArray(l.__sellingPlanAllocations) || !l.__sellingPlanAllocations.length))
				.map((l) => l.merchandiseId)
			: [];

		const allocationsByVariantId = variantIdsNeedingPlans.length
			? await fetchSellingPlanAllocationsByVariantId([...new Set(variantIdsNeedingPlans)])
			: {};

		const checkoutLines = checkoutLinesDraft.map((line) => {
			const { __isFreeGift, __isOneTime, __sellingPlanAllocations, ...rest } = line;
			delete rest.__subproducts;
			delete rest.sellingPlanId;

			if (__isOneTime || __isFreeGift || !sellingPlanId) {
				return { ...rest };
			}

			const allocations =
				(__sellingPlanAllocations?.length ? __sellingPlanAllocations : allocationsByVariantId[line.merchandiseId]) || [];

			const sellingPlanAllocation = allocations
				?.find(({ sellingPlan }) => extractIdFromGid(sellingPlan.id) === sellingPlanId);

			return {
				...rest,
				sellingPlanId: sellingPlanAllocation?.sellingPlan?.id ?? null,
			};
		});

		console.log("[beginCheckout] checkoutLines → cartCreate input", {
			isOneTimePurchase,
			sellingPlanId,
			redirectedDiscount,
			lines: checkoutLines,
		});

		const discountCodes = isOneTimePurchase
			? nonSubscriptionDiscountCodes
			: redirectedDiscount ? [...new Set([...(cart.discountCodes || []), redirectedDiscount])] : (cart.discountCodes || []);

		const isDedicatedOneTimeFlow = hasDedicatedOneTimeFlowItems(items);
		let checkoutCart;
		const checkoutAttributes = [
			...getCheckoutFlowAttributes({ isOneTimePurchase, isDedicatedOneTimeFlow }),
			...getLanguageCartAttributes(language),
		];

		try {
			checkoutCart = await shopifyClient.cart.create({
				lines: checkoutLines,
				discountCodes,
				...(checkoutAttributes.length ? { attributes: checkoutAttributes } : {}),
			});
		} catch (error) {
			console.error("[beginCheckout] cartCreate failed", error);
		}

		const url = buildCheckoutUrl({
			checkoutUrl: checkoutCart?.checkoutUrl || cart.checkoutUrl,
			hostnameOverride: CHECKOUT_DOMAIN,
			language,
			redirectedDiscount,
			queryString,
		});

		console.log("[beginCheckout] redirecting to", {
			checkoutCartId: checkoutCart?.id,
			checkoutUrl: checkoutCart?.checkoutUrl,
			fallbackCheckoutUrl: cart.checkoutUrl,
			finalUrl: url,
		});

		trackBeginCheckout(
			cart,
			isOneTimePurchase,
			isOneTimePurchase ? redirectedDiscount : ""
		);

		if (!url || typeof url !== "string" || !url.startsWith("http")) {
			console.error("[beginCheckout] invalid checkout url, aborting redirect", { url });
			return;
		}

		clearOneTimeSession();
		window.location.assign(url);
	}
);

export const addCartItem = createAsyncThunk(
	"cart/addCartItem",
	async ({ product = {}, variant = {}, source, flowSource = null, oneTime = false }, { getState, dispatch }) => {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);

		try {
			const cartItems = getState().cart.items;
			const attributes = [];
			const lineItems = [];

			if (variant.stock === "one-time" || oneTime) {
				attributes.push({ key: "_oneTime", value: "true" });
				attributes.push(...getOneTimeFlowAttributes(flowSource));
			}
			if (source === "bonus") {
				attributes.push({ key: "_freeGift", value: "true" });
			}
			if (isBundleWithSubProducts(product)) {
				attributes.push({ key: "_bid", value: variant.id });
				attributes.push({ key: "_sdid", value: product._id });
				attributes.push({ key: "_btype", value: "NC" });
				attributes.push({ key: "SKU", value: variant.sku });
				attributes.push({ key: "In bundle", value: product.title });

				const bundleInCart = cartItems[variant.id];

				if (bundleInCart) {
					const lineItems = bundleInCart.subproducts.map((prod) => ({
						id: prod?.tempId,
						quantity: prod.quantity + (+prod.__quantity || 1),
						attributes,
					}));

					const updatedCart = await shopifyClient.cart.updateLineItems(cartId, lineItems);

					return await mapCart(updatedCart);
				}

				const bundleLines = getBundleSubproductLines(product);
				const bundleProductsLen = bundleLines.length;

				if (bundleProductsLen) {
					for (let i = 0; i < bundleProductsLen; i++) {
						const _product = bundleLines[i];

						const _item = {
							merchandiseId: _product.variant.id,
							quantity: _product.__quantity,
							attributes: [
								...attributes,
								{ key: "_cap", value: `${_product.variant.compareAtPrice}` },
							],
						};

						lineItems.push(_item);
					}
				}
			} else {
				lineItems.push({
					merchandiseId: variant.id,
					quantity: 1,
					attributes,
				});
			}

			const updatedCart = await shopifyClient.cart.addLineItems(cartId, lineItems);

			return await mapCart(updatedCart);
		} catch (err) {
			dispatch(fetchCart());
		}
	}
);

export const addCartItems = createAsyncThunk("cart/addCartItems", async (variants, { getState, dispatch }) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);

		const lineItems = variants.map((v) => ({
			merchandiseId: v?.id,
			quantity: v?.quantity ?? 1,
			attributes: (() => {
				const attrs = [];
				const rawWeight = typeof v?.weight === "number" ? v.weight :
					typeof v?.variant?.weight === "number" ? v.variant.weight : undefined;

				if (Number.isFinite(rawWeight)) {
					const weightInGrams = Math.round(rawWeight * 1000);
					attrs.push({ key: "weightInGrams", value: `${weightInGrams}` });
				}

				const isOneTime = v?.stock === "one-time" || v?.oneTime === true;
				if (isOneTime) {
					attrs.push({ key: "_oneTime", value: "true" });
					attrs.push(...getOneTimeFlowAttributes(v?.flowSource));
				}

				return attrs;
			})()
		}));

		const updatedCart = await shopifyClient.cart.addLineItems(cartId, lineItems);
		return await mapCart(updatedCart);

	} catch (err) {
		dispatch(fetchCart());
		return { error: true };
	}
}
);

export const updateCartItem = createAsyncThunk("cart/updateCartItem", async ({ id, quantity, subproducts }, { getState, dispatch }) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);

		const lineItems = subproducts
			? subproducts.map((product) => {
				console.log({ product, quantity });
				return {
					id: product?.tempId,
					quantity: quantity * +product.__quantity,
					attributes: product.attributes,
				};
			})
			: [{ id, quantity }];

		const updatedCart = await shopifyClient.cart.updateLineItems(cartId, lineItems);
		return await mapCart(updatedCart);
	} catch (err) {
		console.log({ err });
		dispatch(fetchCart());
	}
}
);

export const removeCartItem = createAsyncThunk("cart/removeCartItem", async ({ id, subproducts }, { getState, dispatch }) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);

		const lineItems = subproducts
			? subproducts.map((product) => product?.tempId)
			: [id];

		const updatedCart = await shopifyClient.cart.removeLineItems(cartId, lineItems);
		const mappedCart = await mapCart(updatedCart);

		resetOneTimeSessionIfCartEmpty(mappedCart, dispatch);

		return mappedCart;

	} catch (err) {
		dispatch(fetchCart());
	}
}
);

//Remove discount action
export const removeDiscountCode = createAsyncThunk("cart/removeDiscountCode", async (code, { getState, dispatch }) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);
		const { discountCodes } = getState().cart;
		const codes = discountCodes.filter((c) => c !== code);
		const updatedCart = await shopifyClient.cart.updateDiscountCodes(cartId, codes);

		return await mapCart(updatedCart);
	} catch (err) {
		dispatch(fetchCart());
	}
});

export const removeCartItems = createAsyncThunk("cart/removeCartItems", async (lineIds, { getState, dispatch }) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);
		const updatedCart = await shopifyClient.cart.removeLineItems(cartId, lineIds);

		if (lineIds.length >= 2) dispatch(resetRemoveCount());

		const mappedCart = await mapCart(updatedCart);

		resetOneTimeSessionIfCartEmpty(mappedCart, dispatch);

		return mappedCart;
	} catch (err) {
		dispatch(fetchCart());
	}
}
);

export const addDiscountCode = createAsyncThunk("cart/addDiscountCode", async (code, { getState, dispatch }) => {
	try {
		const { shopifyClient } = getState().global;
		const cartId = await getOrCreateCartId(shopifyClient);
		const { discountCodes } = getState().cart;
		const codes = [code, ...discountCodes];
		const updatedCart = await shopifyClient.cart.updateDiscountCodes(cartId, codes);
		return await mapCart(updatedCart);
	} catch (err) {
		dispatch(fetchCart());
	}
}
);

// Utility function to select and save a box
export const selectAndSaveBox = (box) => async (dispatch) => {
	dispatch(selectBox(box));
	localStorage.setItem(activeBoxKey, JSON.stringify(box));
};

export const selectAndSaveFrequency = (frequency, frequencies = []) => (dispatch) => {

	const frequencyPayload = frequencies.length
		? frequencies.find(f => f.interval === frequency.interval && f.intervalCount === frequency.intervalCount) || frequency
		: frequency;

	if (!frequencyPayload) return;

	dispatch(setDeliveryFrequency(frequencyPayload));
	localStorage.setItem(frequencyKey, JSON.stringify(frequencyPayload));
};


export const updateActiveBox = (boxes, frequencies = []) => (dispatch, state) => {
	const { activeBox, totalProgress } = state().cart;

	let minBox = findClosestBox(boxes, totalProgress);

	// A/B Test 1165 V1 hides the 8 and 10 meal boxes.
	if (isVariant1165V1() && (minBox.size.value === 8 || minBox.size.value === 10)) {
		minBox = boxes.find(box => box.size.value === 6) ?? minBox;
	}
	// A/B Test 1165 V2 hides the 6 and 10 meal boxes.
	if (isVariant1165V2() && (minBox.size.value === 6 || minBox.size.value === 10)) {
		minBox = boxes.find(box => box.size.value === 8) ?? minBox;
	}

	dispatch(selectMinBox(minBox));

	const parseLocalStorage = (key) => {
		const value = localStorage.getItem(key);
		if (!value || value === "undefined") return null;
		try { return JSON.parse(value); } catch { return null; }
	};

	const savedActiveBox = parseLocalStorage(activeBoxKey);
	const savedFrequency = parseLocalStorage(frequencyKey) || {};
	const defaultBox = boxes.find(box => box.size.value === 16);
	const defaultFreq = frequencies.find(f => f.interval === "WEEK" && f.intervalCount === 4) || frequencies[0];

	// If no active box is selected and no saved box exists, select the default box
	if (!savedActiveBox && totalProgress <= defaultBox.size.value) {
		dispatch(selectAndSaveBox(defaultBox));
		if (defaultFreq) dispatch(selectAndSaveFrequency(defaultFreq, frequencies));
		return;
	}

	// If no active box is selected, select the saved box from localStorage if it exists
	if (!activeBox && savedActiveBox?.price) {
		const frequency = (savedActiveBox.size.value % savedFrequency.intervalCount === 0) ? savedFrequency : defaultFreq;
		if (frequency) dispatch(selectAndSaveFrequency(frequency, frequencies));
		dispatch(selectAndSaveBox(savedActiveBox));
		return;
	}

	// A/B Test
	const shouldUpdateBox = activeBox && (minBox.size.value > activeBox?.size.value ||
		(minBox.size.value < activeBox?.size.value && !activeBox.userSelected));

	if (shouldUpdateBox) dispatch(selectAndSaveBox(minBox));
};
